<template lang="pug">
    .play-box(:class="{'play-box--warning': isVerticalScreen}")
        .warning(v-if="isVerticalScreen")
            template
                svg#rotate(
                    version='1.1',
                    xmlns='http://www.w3.org/2000/svg',
                    x='0px', y='0px', width='40px', height='40px',
                    viewBox='0 0 612 612',
                    style='enable-background:new 0 0 612 612;',
                    xml:space='preserve'
                )
                    rotation-icon
                div
                    | {{ $i18n.t('map.map_mobile_rotateText') }}
        template(v-else)
            template(v-if="!isLoading")
                game-header(
                    @soundStateChanged="headerKey = !headerKey",
                    :key="headerKey",
                    :time="getTime",
                    :slug="type",
                    :game-type="parameters && parameters.courseType",
                    :is-homework="!!exerciseId",
                )
                .play-box__game(
                    v-if="!gameFinished  && gameStarted"
                    :class="'play-box__game--tables'"
                )
                    memorize-table(
                        v-if="numbers.length"
                        :numbers="numbers"
                        :is-shown="isTableShown"
                        :display-speed="displaySpeed"
                        @handleClick ='handleClick'
                        :key="numbers && `${numbers.length} x ${numbers[0].length}`"
                    )
                .game-info__container(v-if="!gameFinished")
                    .game-info__block
                        game-info(
                            :score="{left: answers.good, right: answers.bad}"
                            :time="getTime"
                            :parameters="parameters"
                            :game-allows-tips="true"
                        )
                    .memorize__buttons-container(v-show='isTableShown && displaySpeed === 0')
                        cs-button.memorize__button(
                            color="blue"
                            @click='isTableShown = !isTableShown'
                            v-focus=''
                        )
                            | {{ memorizedButtonName }}
                .play-box__end-container(v-if="gameFinished")
                    fireworks(
                        v-if="gameFinished"
                        :is-mobile="isMobile"
                    )
                    .play-box__reaction-container
                        .play-box__time
                            | {{ timeExecutionString }}
                        .play-box__reaction
                            .play-box__reaction-side.play-box__reaction-side--correct
                                div
                                    | {{ $i18n.t('trainer.trainer_gamePlayBox_correct') }}
                                .play-box__reaction-count
                                    | {{ answers.good }}
                            result-image.play-box__image(:status="passedSuccessfully ? 'success' : 'failure'")
                            .play-box__reaction-side.play-box__reaction-side--incorrect
                                div
                                    | {{ $i18n.t('trainer.trainer_gamePlayBox_wrong') }}
                                .play-box__reaction-count
                                    | {{ answers.bad }}

                    result-buttons(
                        :exercise-id="exerciseId"
                        :games-type="parameters.courseType"
                        show-restart-button
                        @restartGame="restartGame"
                    )

</template>

<script>
    import MemorizeTable from './MemorizeTable.vue';
    import { shuffle, chunk, cloneDeep } from 'lodash';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import EndGameMixin from '../../../../utils/trainer/endGame';
    import GameHeader from '../../GameHeader.vue';
    import GameInfo from '../GameInfo.vue';
    import Fireworks from '../Fireworks.vue';
    import RotationIcon from '../../../../components/svg/RotationIcon.vue';
    import ResultImage from '../ResultImage.vue';
    import CalculatingAnswers from '../../CalculatingAnswersModal.vue';
    import MemorizeConfig from '../../configs/MemorizeConfig';
    import timer from '../../../../utils/trainer/timer';
    import MobileDetect from 'mobile-detect';

    function DEFAULT_DATA() {
        return {
            difficult: null,
            timeLimit: 0,
            displaySpeed: 0,
            columns: null,
            rows: null,
            selectedLevel: 1,
            amountOfNumbers: 3,
            numbers: [],
            correctAnswers: [],
            isTableShown: false,
            health: 3,
            correctAnswersInOneSubLevel: 0,
            subLevelsAmount: 0,
            subLevel: 1,
            level: null,
            startAmountOfLevels: MemorizeConfig.difficulties.length,
            amountOfLevels: 0,
            filledCells: [],
            godMode: false,
            passedSuccessfully: null,
            memorizeTableCells: [],
            timeLimitSeconds: 0,
            answers: {
                good: 0,
                bad: 0,
            },
            isFetched: false,
        };
    }

    export default {
        mixins: [playBoxGameMixin, EndGameMixin, timer],
        created() {
            window.addEventListener('beforeunload', this.beforeTabClosed);
        },
        mounted() {
            this.isTablet = Boolean(new MobileDetect(window.navigator.userAgent).tablet());
            this.$nextTick(() => {
                !this.isVerticalScreen && !this.isFetched && this.fetchData();
            });
        },
        updated() {
            !this.isVerticalScreen && !this.isFetched && this.fetchData();
        },
        beforeDestroy() {
            this.beforeTabClosed();
            window.removeEventListener('beforeunload', this.beforeTabClosed);
        },
        watch: {
            amountOfLevels(value) {
                if (value === 0) {
                    this.passedSuccessfully = true;
                    this.finishGame();
                }
            },
            health(value) {
                if (value === 0) {
                    this.passedSuccessfully = false;
                    this.finishGame();
                }
            },
            subLevel(newValue) {
                if (newValue) {
                    this.health = 3;
                }
                if (newValue > this.subLevelsAmount) {
                    this.subLevel = 1;
                    this.level++;
                } else {
                    this.filledCells.forEach(cell => (cell.innerHTML = ''));
                    this.isTableShown = true;
                    this.numbers = this.generateTable();
                    this.setDisplaySpeed();
                }
            },
            level(newValue) {
                if (newValue) {
                    this.filledCells.forEach(cell => (cell.innerHTML = ''));
                    this.correctAnswers.length = 0;
                    this.isTableShown = true;
                    this.columns = MemorizeConfig.difficulties[this.level - 1].columns;
                    this.rows = MemorizeConfig.difficulties[this.level - 1].rows;
                    this.numbers = this.generateTable();
                    this.setDisplaySpeed();
                }
            },
            timeLimitSeconds(val) {
                if (val >= this.timeLimit * 60 && this.timeLimit !== 0 && !this.gameFinished) {
                    this.passedSuccessfully = this.answers.good > this.answers.bad;
                    this.finishGame();
                }
            },
            timeLimit(val) {
                if (val > 0) {
                    this.setTimeLimitTimer();
                }
            },
        },
        data() {
            return DEFAULT_DATA();
        },
        methods: {
            prepareForGeneratingTable() {
                this.filledCells.forEach(cell => (cell.innerHTML = ''));
                this.correctAnswers.length = 0;
                this.isTableShown = true;
                this.columns = MemorizeConfig.difficulties[this.level - 1].columns;
                this.rows = MemorizeConfig.difficulties[this.level - 1].rows;
                this.numbers = this.generateTable();
                this.setDisplaySpeed();
            },
            restartGame() {
                this.beforeGameRestart && this.beforeGameRestart();
                Object.assign(this, DEFAULT_DATA());
                this.assignDataFromParameters();
                clearTimeout(this.counterTimeout);
                this.resetTimer();
                this.fetchData();
                this.prepareForGeneratingTable();
            },
            fetchData() {
                this.isFetched = true;
                this.getExercise()
                    .then(() => {
                        this.assignDataFromParameters();

                        this.$createjs.Sound.removeAllSounds();
                        this.$createjs.Sound.removeAllEventListeners();
                        this.$createjs.Sound.alternateExtensions = ['mp3', 'ogg'];
                        this.$createjs.Sound.registerSounds(this.soundObjects);

                        this.beforeStart();
                        this.isLoading = false;
                    })
                    .catch(() => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            beforeStart() {
                this.gameFinished = false;
                this.gameStarted = true;
                !this.timer && this.setTimer();
                this.$nextTick(() => {
                    this.setDisplaySpeed();
                });
            },
            assignDataFromParameters() {
                this.timeLimit = this.parameters.timeLimit;
                this.displaySpeed = this.parameters.displaySpeed;
                this.level = this.parameters.difficult.level;
                this.columns = this.parameters.difficult.columns;
                this.rows = this.parameters.difficult.rows;
                this.godMode = this.timeLimit > 0;
            },
            generateTable() {
                this.subLevelsAmount = this.rows;
                this.amountOfLevels = this.startAmountOfLevels - this.level;
                if (this.level !== 1) {
                    this.amountOfNumbers = this.columns + this.subLevel;
                } else if (this.level === 1 && this.subLevel !== 1) {
                    this.amountOfNumbers = 4;
                }
                const availableNumbersArray = Array.from({ length: this.amountOfNumbers }, (_, i) => i + 1);
                this.correctAnswers = [...availableNumbersArray];
                const restLengthToFillWithNull = this.rows * this.columns - availableNumbersArray.length;
                const shuffledNumbers = shuffle([
                    ...availableNumbersArray,
                    ...Array(restLengthToFillWithNull).fill(null),
                ]);
                return chunk(shuffledNumbers, this.columns);
            },
            handleClick(selectedCell, num) {
                if (this.correctAnswers.length && this.correctAnswers[0] === num) {
                    selectedCell.classList.add('memorize-table__press-item_green');
                    this.$createjs.Sound.play('clickCorrect');
                    this.filledCells.push(selectedCell);
                    selectedCell.innerHTML = num;
                    this.correctAnswers.shift();
                    this.answers.good++;
                    if (!(this.answers.good % this.amountOfNumbers)) {
                        this.correctAnswersInOneSubLevel++;
                    }
                    if (this.correctAnswers.length === 0) {
                        this.checkForSwitchLevel();
                    }
                } else {
                    selectedCell.classList.add('shake', 'memorize-table__press-item_white');
                    this.$createjs.Sound.play('clickIncorrect');
                    setTimeout(() => {
                        selectedCell.classList.remove('shake', 'memorize-table__press-item_white');
                    }, 800);
                    this.answers.bad++;
                    if (!this.godMode) {
                        this.health--;
                    }
                }
            },
            checkForSwitchLevel() {
                if (this.correctAnswersInOneSubLevel === 3) {
                    this.subLevel++;
                    this.correctAnswersInOneSubLevel = 0;
                } else {
                    this.filledCells.forEach(cell => (cell.innerHTML = ''));
                    this.isTableShown = true;
                    this.numbers = this.generateTable();
                    this.setDisplaySpeed();
                }
            },
            setDisplaySpeed() {
                if (this.displaySpeed > 0) {
                    setTimeout(() => {
                        this.isTableShown = false;
                    }, this.displaySpeed * 1000);
                } else {
                    this.isTableShown = true;
                }
            },
            refreshGameStatBeforeSend() {
                this.gameStat.attempts = this.answers.good + this.answers.bad;
                this.gameStat.answers = this.answers.good;
                this.gameStat.chainOfAttempts = [];
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = this.gameFinished;
                this.gameStat.parameters = cloneDeep(this.parameters);
            },
            calculatePercent() {
                let total = this.answers.good + this.answers.bad;
                if (total > 0) {
                    return parseInt((this.answers.good / total) * 100);
                }
                return 0;
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
            finishGame() {
                clearTimeout(this.timeLimitTimer);
                clearTimeout(this.timer);
                this.gameFinished = true;
                this.sendStat();
            },
            beforeTabClosed() {
                if (this.gameStarted && this.user.type === 'student') {
                    this.finishGame();
                }
                return null;
            },
            setTimeLimitTimer() {
                this.timeLimitSeconds = this.timeLimitSeconds + 1;
                this.timeLimitTimer = setTimeout(this.setTimeLimitTimer, 1000);
            },
        },
        computed: {
            timeExecutionString() {
                return (
                    this.$i18n.t('progress.progress_table_executionTime') +
                    ': ' +
                    this.time +
                    ' ' +
                    this.$i18n.tc('olympiads.olympiads_timeDeclination_seconds', this.time)
                );
            },
            memorizedButtonName() {
                return this.$i18n.t('trainer.trainer_gamePlayBox_memorizedButton', {
                    mode: this.$i18n.t('trainer.trainer_gamePlayBox_memorizedButton_all'),
                });
            },
        },
        components: {
            Fireworks,
            GameInfo,
            GameHeader,
            MemorizeTable,
            RotationIcon,
            CalculatingAnswers,
            ResultImage,
        },
    };
</script>

<style scoped lang="scss">
    @import '@/assets/sass/games/playbox.scss';

    .play-box {
        height: 100%;
    }

    .play-box--warning {
        display: table;
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }

    .play-box__game {
        display: flex;
        justify-content: center;
        align-items: stretch;
        flex: 1 1 auto;
    }

    .play-box__game--tables {
        justify-content: space-between;
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        width: 100%;
    }

    .play-box__time {
        font-weight: 500;
        font-size: 32px;
        line-height: 38px;
        text-align: center;
        color: $hsl-color-green-58;
        margin-bottom: 30px;
    }

    .play-box__image {
        max-width: 100%;
        display: none;
        @include media($sm) {
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            align-items: center;
            min-height: 300px;
        }
        @include media($md) {
            margin-bottom: 40px;
            min-height: 450px;
        }
        @include media($xlg) {
            min-height: 600px;
        }
    }

    .memorize__button {
        width: 150px;
        @include media($lg) {
            width: 200px;
        }
    }
    .memorize__buttons-container {
        display: flex;
        justify-content: center;
        text-align: center;
        margin: 40px 0;
        :nth-child(2n) {
            margin-left: 20px;
        }
    }
    .play-box__reaction {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        flex-grow: 1;
        flex-shrink: 1;
        width: 100%;
        @include stack-space($space-base);
        flex-direction: row;
        @include media($md) {
            flex-wrap: nowrap;
        }
        @media screen and (max-width: 655px) {
            justify-content: space-between;
        }
    }

    .play-box__reaction-side {
        align-self: normal;
        font-weight: 500;
        font-size: 20px;
        @include media($sm) {
            width: auto;
            font-size: 20px;
        }
        @include media($md) {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            flex-grow: 1;
            align-self: center;
            font-size: 28px;
        }
        @media screen and ($md) and (max-width: 800px) {
            font-size: 18px;
        }
        @include media($lg) {
            font-size: 32px;
        }
        @include media($xlg) {
            font-size: 42px;
        }
        color: $hsl-color-green-58;
        @include stack-space($space-base);
        @include media($sm) {
            @include stack-space(0);
        }
        @media screen and (max-width: 768px) {
            width: 50%;
        }
    }

    .play-box__reaction-side--correct {
        color: $hsl-color-green-58;
    }

    .play-box__reaction-side--incorrect {
        color: $hsl-color-red-62;
    }
</style>
